import React, { useCallback, useEffect, useRef } from 'react';

import { addModifiers } from '../../shared/helpers';
import { useScramble, UseScrambleProps } from 'use-scramble';
import { useIntl } from 'react-intl';
import { formatMessageHelper } from '../../shared/helpers/internationalization';
import { SUPPORTED_BROWSER_LANGUAGE_CODES } from '../../shared/services/i18n';

function getDefaultScrambleSettings(language: string): UseScrambleProps {
  if (language === SUPPORTED_BROWSER_LANGUAGE_CODES.english || language === SUPPORTED_BROWSER_LANGUAGE_CODES.vietnamese) {
    return {
      tick: 2,
      speed: 0.3,
      playOnMount: false,
    };
  }

  // Different settings for non-latins languages because it may have fewer characters
  // so the animation should be slower
  return {
    tick: 5,
    speed: 0.4,
    playOnMount: false,
  };
}

interface IQCTitleProps {
  language: string;
}

function IQCTitleImp({ language }: IQCTitleProps) {
  const intl = useIntl();

  const isPrefersReducedMotion = window?.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const headingRef = useRef<HTMLHeadingElement | null>(null);

  // const scrambleTitle1 = useScramble({
  //   ...getDefaultScrambleSettings(language),
  //   text: formatMessageHelper(intl, 'iqc.title.1'),
  //   onAnimationEnd: () => scrambleTitle2.replay(),
  // });

  const scrambleTitle2 = useScramble({
    ...getDefaultScrambleSettings(language),
    playOnMount: true,
    text: formatMessageHelper(intl, 'iqc.title.2'),
    onAnimationEnd: () => scrambleTitle3.replay(),
  });

  const scrambleTitle3 = useScramble({
    ...getDefaultScrambleSettings(language),
    text: formatMessageHelper(intl, 'iqc.title.3'),
    onAnimationEnd: () => removeHeightOfHeading(),
  });

  const removeHeightOfHeading = useCallback(() => {
    const heading = headingRef.current;
    if (!heading) return;
    heading.style.removeProperty('height');
    heading.style.removeProperty('overflow');
  }, []);

  useEffect(() => {
    function runTextScramble() {
      const heading = headingRef.current;
      if (!heading) return;
      heading.style.setProperty('overflow', 'hidden');
      heading.style.setProperty('height', `${heading.offsetHeight}px`);
      // setTimeout(() => scrambleTitle1.replay(), 0);
    }

    if (!isPrefersReducedMotion) {
      const id = setTimeout(runTextScramble, 1500);
      return () => clearTimeout(id);
    }
  }, []);

  if (language === SUPPORTED_BROWSER_LANGUAGE_CODES.english || language === SUPPORTED_BROWSER_LANGUAGE_CODES.vietnamese) {
    return (
      <h1 ref={headingRef} className={addModifiers('iqc__title', ['chakra-petch'])}>
        <span>{formatMessageHelper(intl, 'iqc.title.1')}</span>
        <br />
        <span ref={scrambleTitle2.ref} className="gradient strong" />
      </h1>
    );
  }

  if (language === SUPPORTED_BROWSER_LANGUAGE_CODES.korean) {
    return (
      <h1 ref={headingRef} className={addModifiers('iqc__title', ['chakra-petch', language])}>
        <span>{formatMessageHelper(intl, 'iqc.title.1')}</span>
        <br />
        <span ref={scrambleTitle2.ref} className="gradient strong" />
        <span ref={scrambleTitle3.ref} />
      </h1>
    );
  }

  return (
    <h1 ref={headingRef} className={addModifiers('iqc__title', ['chakra-petch', language])}>
      <span>{formatMessageHelper(intl, 'iqc.title.1')}</span>
      <span ref={scrambleTitle2.ref} className="gradient strong" />
      <span ref={scrambleTitle3.ref} />
    </h1>
  );
}

export const IQCTitle = React.memo(IQCTitleImp);
